import api from './api'

export type GlobalSearchKind =
  | 'vector_feature'
  | 'vector_layer'
  | 'raster'
  | 'message'

export type RasterSource = 'lidar_pipeline' | 'gis_upload'

export type RasterType =
  | 'snow_depth'
  | 'dtm'
  | 'dsm'
  | 'ortho_rgb'
  | 'ortho_ir'
  | 'custom'

export type GlobalSearchFilters = {
  q?: string
  types?: ReadonlyArray<GlobalSearchKind>
  createdAfter?: string | null
  createdBefore?: string | null
  creatorId?: number | null
  rasterSources?: ReadonlyArray<RasterSource>
  rasterTypes?: ReadonlyArray<RasterType>
  folderId?: number | null
  limitPerType?: number
}

export type VectorFeatureHitDto = {
  kind: 'vector_feature'
  feature_id: string | number
  layer_id: number
  layer_name: string | null
  geometry: GeoJSON.Geometry
  properties: Record<string, unknown> | null
  layer_creator_user_id: number | null
  layer_creator_username: string | null
}

export type VectorLayerHitDto = {
  kind: 'vector_layer'
  layer_id: number
  name: string | null
  description: string | null
  category: string | null
  folder_id: number | null
  user_id: number | null
  creator_username: string | null
  created_at: string | null
  feature_count: number | null
  bbox: GeoJSON.Geometry | null
}

export type RasterHitDto = {
  kind: 'raster'
  raster_id: number
  name: string | null
  file_name: string | null
  raster_type: RasterType
  source: RasterSource
  created_at: string | null
  s3_url: string | null
}

export type MessageHitDto = {
  kind: 'message'
  marker_id: number
  public_id: string
  description: string | null
  category: string | null
  latitude: number
  longitude: number
  created_at: string | null
  created_by_user_id: number | null
  creator_username: string | null
}

export type GlobalSearchHitDto =
  | VectorFeatureHitDto
  | VectorLayerHitDto
  | RasterHitDto
  | MessageHitDto

export type GlobalSearchResponse = {
  hits: GlobalSearchHitDto[]
  totals: Record<GlobalSearchKind, number>
}

// csv: comma-joined string for the query param.
const csv = (arr: ReadonlyArray<string> | undefined): string | undefined =>
  arr && arr.length > 0 ? arr.join(',') : undefined

const stringifyDate = (v: string | null | undefined): string | undefined =>
  v == null || v === '' ? undefined : v

const stringifyInt = (v: number | null | undefined): number | undefined =>
  v == null || !Number.isFinite(v) ? undefined : v

export type SearchCreator = { id: number; username: string }

export async function listSearchCreators(opts: {
  signal?: AbortSignal
} = {}): Promise<SearchCreator[]> {
  const response = await api.get('/search/creators', { signal: opts.signal })
  const data = response.data as { creators?: SearchCreator[] }
  return data.creators ?? []
}

export async function globalSearch(
  filters: GlobalSearchFilters,
  opts: { signal?: AbortSignal } = {},
): Promise<GlobalSearchResponse> {
  const params: Record<string, string | number> = {}
  if (filters.q != null && filters.q !== '') params.q = filters.q
  const t = csv(filters.types)
  if (t) params.types = t
  const after = stringifyDate(filters.createdAfter)
  if (after) params.created_after = after
  const before = stringifyDate(filters.createdBefore)
  if (before) params.created_before = before
  const creator = stringifyInt(filters.creatorId)
  if (creator != null) params.creator_id = creator
  const rs = csv(filters.rasterSources)
  if (rs) params.raster_sources = rs
  const rt = csv(filters.rasterTypes)
  if (rt) params.raster_types = rt
  const folder = stringifyInt(filters.folderId)
  if (folder != null) params.folder_id = folder
  const limit = stringifyInt(filters.limitPerType)
  if (limit != null) params.limit_per_type = limit

  const response = await api.get('/search', {
    params,
    signal: opts.signal,
  })
  return response.data as GlobalSearchResponse
}
